import React from 'react';
import { ESnapshotExists, EOrganization, ENoticeDraft, EUser } from 'lib/types';
import { Form } from 'lib/components/Form';
import { LoadingSpinner } from 'lib/components/LoadingSpinner';
import { actions } from './NoticeDelivery/deliveryRecipients.slice';
import ScrollStep from './ScrollStep';
import { SCHEDULE_NOTICE } from './helpers/calculatePlacementSteps';
import { ReferenceIdForm, useReferenceIdState } from './ReferenceIdForm';
import {
  InvoiceRecipientForm,
  useInvoiceRecipientFormState
} from './InvoiceRecipientForm';
import {
  ConfirmScheduleForm,
  useConfirmScheduleState
} from './ConfirmScheduleForm';
import { AffidavitRecipientsForm } from './NoticeDelivery/AffidavitRecipientsForm';
import { useDeliveryRecipientsState } from './NoticeDelivery/useDeliveryRecipientsState';
import { useFetchCustomer } from './hooks/useFetchCustomer';
import { useFetchCustomerOrganization } from './hooks/useFetchCustomerOrganization';

type ScheduleNoticeStepProps = {
  publisherOrganization?: ESnapshotExists<EOrganization>;
  notice: ESnapshotExists<ENoticeDraft>;
  filer?: ESnapshotExists<EUser>;
  next: () => void;
  previous: () => void;
  onDisabledStepClick: (stepId: string) => void;
};

export function ScheduleNoticeStep({
  publisherOrganization,
  notice,
  filer,
  next,
  previous,
  onDisabledStepClick
}: ScheduleNoticeStepProps) {
  const stepId = SCHEDULE_NOTICE;
  const customer = useFetchCustomer();
  const customerOrganization = useFetchCustomerOrganization();

  const { referenceId, setReferenceId, saveReferenceId } =
    useReferenceIdState();
  const {
    isStepComplete: invoiceRecipientComplete,
    onExit: updateInvoiceRecipientInformation,
    email,
    setEmail,
    addressLine1,
    setAddressLine1,
    addressLine2,
    setAddressLine2,
    city,
    setCity,
    state,
    setState,
    zip,
    setZip,
    sendToEmail,
    setSendToEmail,
    billTo,
    setBillTo,
    purchaseOrderNumber,
    setPurchaseOrderNumber
  } = useInvoiceRecipientFormState();
  const { onExit: confirmSchedule, complete: confirmScheduleComplete } =
    useConfirmScheduleState({
      newspaper: publisherOrganization,
      notice
    });
  const {
    state: deliveryAddonState,
    dispatch: affidavitStateDispatch,
    validateDeliveryForm,
    complete: affidavitMailingComplete,
    customerMustReceiveMailedAffidavit,
    noticeWillRequireWetSignature,
    affidavitsManagedByColumn,
    wetSigOffered,
    affidavitDisabled,
    affidavitSettingsLoading
  } = useDeliveryRecipientsState({
    stepId,
    publisherOrganization,
    draft: notice,
    filer
  });

  const title = 'Tell us a bit more about your notice';
  const caption = `Provide details to help ${
    publisherOrganization?.data().name
  } publish this notice correctly.`;

  const complete =
    confirmScheduleComplete &&
    invoiceRecipientComplete &&
    affidavitMailingComplete;

  async function handleNext() {
    saveReferenceId();
    updateInvoiceRecipientInformation();
    confirmSchedule();
    const { success: affidavitAddressValidationSuccess } =
      await validateDeliveryForm();

    if (affidavitAddressValidationSuccess) {
      next();
    } else {
      // Scroll to the first visible error so the user knows what to fix
      setTimeout(() => {
        const stepEl = document.getElementById(stepId);
        const firstError = stepEl?.querySelector('.text-red-600');
        if (firstError) {
          firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 0);
    }
  }

  const formId = `${stepId}-referenceId-form`;
  return (
    <ScrollStep
      id={stepId}
      formId={formId}
      title={title}
      caption={caption}
      previous={previous}
      onDisabledStepClick={onDisabledStepClick}
      complete={complete}
    >
      <div className="space-y-12">
        <Form id={formId} onSubmit={handleNext}>
          <h2 className="inline-block text-lg border-t-4 border-column-gray-200 font-bold pt-4 my-5">
            Notice name
          </h2>
          <ReferenceIdForm
            referenceId={referenceId}
            setReferenceId={setReferenceId}
          />
        </Form>

        <div>
          <h2 className="inline-block text-lg border-t-4 border-column-gray-200 font-bold pt-4 my-5">
            Publication dates
          </h2>
          <ConfirmScheduleForm
            notice={notice}
            newspaper={publisherOrganization}
            customer={customer}
            customerOrganization={customerOrganization}
          />
        </div>

        <div>
          <h2 className="inline-block text-lg border-t-4 border-column-gray-200 font-bold pt-4 my-5">
            Invoice preferences
          </h2>

          <InvoiceRecipientForm
            publisherOrganization={publisherOrganization}
            email={email}
            setEmail={setEmail}
            addressLine1={addressLine1}
            setAddressLine1={setAddressLine1}
            addressLine2={addressLine2}
            setAddressLine2={setAddressLine2}
            city={city}
            setCity={setCity}
            state={state}
            setState={setState}
            zip={zip}
            setZip={setZip}
            sendToEmail={sendToEmail}
            setSendToEmail={setSendToEmail}
            billTo={billTo}
            setBillTo={setBillTo}
            purchaseOrderNumber={purchaseOrderNumber}
            setPurchaseOrderNumber={setPurchaseOrderNumber}
            invoiceMailingRecipients={
              deliveryAddonState.invoiceMailingRecipients
            }
            onInvoiceMailingRecipientsChange={recipients =>
              affidavitStateDispatch(
                actions.setInvoiceMailingRecipients(recipients)
              )
            }
            pricingConfig={
              publisherOrganization?.data().noticeAddOnPricing ?? {}
            }
            invoiceMailingValidationErrors={
              deliveryAddonState.invoiceRecipientValidationErrors
            }
            showInvoiceMailingErrors={deliveryAddonState.showErrors}
          />
        </div>
        <div>
          <h2 className="inline-block text-lg border-t-4 border-column-gray-200 font-bold pt-4 my-5">
            Affidavit preferences
          </h2>
          {affidavitSettingsLoading ? (
            // The affidavit options depend on the cascaded settings (newspaper →
            // notice type → customer), which resolve asynchronously. Hold the
            // section until they land so the form never renders — or seeds state —
            // from a guessed value.
            <LoadingSpinner />
          ) : (
            <AffidavitRecipientsForm
              state={deliveryAddonState}
              dispatch={affidavitStateDispatch}
              publisherOrganization={publisherOrganization}
              customerMustReceiveMailedAffidavit={
                customerMustReceiveMailedAffidavit
              }
              noticeWillRequireWetSignature={noticeWillRequireWetSignature}
              affidavitsManagedByColumn={affidavitsManagedByColumn}
              wetSigOffered={wetSigOffered}
              affidavitDisabled={affidavitDisabled}
            />
          )}
        </div>
      </div>
    </ScrollStep>
  );
}
